"use client";

import { useState } from "react";
import { ArrowRightLeft, ChevronDown, ChevronUp, ListChecks, Trash2, X } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { usePipelineStages, useBulkUpdateLeadStatus } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { showToast } from "@/app/_module/utils/show-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { BulkSourceOptions, BulkTagOptions } from "@/app/_module/components/BulkLeadOptions";
import { useToast } from '@/app/_module/components/ui/use-toast';

/**
 * What a plan WITHOUT a phone (Lite) gets instead of the dialer widget when
 * leads are selected in the table: the same selection the dialer's Lead Queue
 * would hold, with the two things that still make sense — move the leads to a
 * pipeline stage, or delete them. No keypad, no Power Dial, no Multi-Line.
 *
 * Rides on UnifiedDialerContext like the dialer does: MultiSelectActions
 * pushes the table selection into `selectedLeads`, and the delete confirmation
 * dialog it owns opens from `requestDeleteSelectedLeads`.
 */
export function LiteSelectionWidget() {
  const { selectedLeads, clearSelectedLeads, requestDeleteSelectedLeads } = useUnifiedDialer();
  const { toast } = useToast();
  const { data: pipelineStages } = usePipelineStages();
  const bulkUpdateStatus = useBulkUpdateLeadStatus();
  const [collapsed, setCollapsed] = useState(false);

  const count = selectedLeads.length;
  if (count === 0) return null;

  const isShell = typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const moveTo = (stage: { id: string; name: string; systemValue?: string | null }) => {
    const leadIds = selectedLeads.map((l) => l.id.toString());
    bulkUpdateStatus.mutate(
      { leadIds, newStatus: stage.systemValue || stage.name },
      {
        onSuccess: () => {
          showToast(`Moved ${count.toLocaleString()} lead${count === 1 ? "" : "s"} to ${stage.name}`, "success");
          clearSelectedLeads();
        },
      },
    );
  };

  const leadIds = () => selectedLeads.map((l) => l.id);
  const clearSource = async () => {
    try {
      await api.post({ url: getApiUrl("/leads/bulk-update-source"), body: { leadIds: leadIds(), listName: "" }, auth: true });
      showToast(`Cleared lead source from ${count} leads`, "success");
    } catch {}
  };
  const clearTags = async () => {
    try {
      await api.post({ url: getApiUrl("/leads/bulk-clear-tags"), body: { leadIds: leadIds() }, auth: true });
      showToast(`Cleared all tags from ${count} leads`, "success");
    } catch {}
  };

  return (
    <div
      data-testid="lite-selection-widget"
      className={
        isShell
          ? "fixed left-3 right-3 z-50 rounded-2xl border border-black/10 bg-background shadow-2xl"
          : "fixed bottom-4 right-4 z-50 w-[300px] rounded-xl border-2 border-primary bg-background shadow-2xl shadow-primary/30"
      }
      style={isShell ? { bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)" } : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b">
        <ListChecks className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-sm font-semibold flex-1 truncate">
          {count.toLocaleString()} lead{count === 1 ? "" : "s"} selected
        </span>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="p-1 rounded hover:bg-muted"
          aria-label={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button type="button" onClick={clearSelectedLeads} className="p-1 rounded hover:bg-muted" aria-label="Clear selection">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="w-full justify-start gap-3" disabled={bulkUpdateStatus.isPending}>
                <ArrowRightLeft className="h-4 w-4" />
                {bulkUpdateStatus.isPending ? "Updating…" : "Bulk actions"}
              </Button>
            </DropdownMenuTrigger>
            {/* Same menu as the dialer's Lead Queue: pipeline, source, tags. */}
            <DropdownMenuContent align="end" className="w-56 max-h-[400px] overflow-y-auto" sideOffset={4}>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Move {count.toLocaleString()} to pipeline
              </div>
              {pipelineStages?.map((stage: any) => (
                <DropdownMenuItem key={stage.id} onClick={() => moveTo(stage)} className="cursor-pointer gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                  <span className="text-sm truncate">{stage.name}</span>
                </DropdownMenuItem>
              ))}

              <div className="border-t my-1" />
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Set lead source
              </div>
              <BulkSourceOptions selectedLeads={selectedLeads} />
              <DropdownMenuItem onClick={clearSource} className="cursor-pointer gap-2 text-red-500">
                <X className="h-3.5 w-3.5" />
                <span className="text-sm">Clear source</span>
              </DropdownMenuItem>

              <div className="border-t my-1" />
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Add tag
              </div>
              <BulkTagOptions selectedLeads={selectedLeads} />
              <DropdownMenuItem onClick={clearTags} className="cursor-pointer gap-2 text-red-500">
                <X className="h-3.5 w-3.5" />
                <span className="text-sm">Clear all tags</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            className="w-full justify-start gap-3 border-red-200 hover:bg-red-50 text-red-600"
            onClick={() => {
            if (!requestDeleteSelectedLeads()) {
              toast({ title: 'Open the Leads page to delete', description: 'Selected leads can only be deleted from the Leads page, where the confirmation shows.' });
            }
          }}
          >
            <Trash2 className="h-4 w-4" />
            Delete selected leads
          </Button>
        </div>
      )}
    </div>
  );
}
